/**
 * The cue style button (1.9.0): colour swatches (white, yellow, cyan, green, magenta) and a custom colour, the
 * background opacity (0–100 %), the size step, and "Use the track style". role=dialog (Esc closes it).
 */
import { Palette } from 'lucide-react';
import type { CueStyle, CueStylePatch, ResolvedCueStyle } from '@prism/contracts';
import { Popover } from './Popover';

export const CUE_COLOR_SWATCHES: ReadonlyArray<{ label: string; color: string }> = [
  { label: 'White', color: '#ffffff' },
  { label: 'Yellow', color: '#ffff00' },
  { label: 'Cyan', color: '#00ffff' },
  { label: 'Green', color: '#00ff00' },
  { label: 'Magenta', color: '#ff00ff' },
];

/** The size steps, as scales of the default size (75–150 %). */
export const CUE_SIZE_STEPS: readonly number[] = [0.75, 0.9, 1, 1.15, 1.3, 1.5];

export interface CueStylePopoverProps {
  /** The selected cue's own style (several cues: the fields they share). */
  value: CueStyle;
  /** What the track style gives, for the fields the cue does not set. */
  track: ResolvedCueStyle;
  /** A change for the selected cues; null: "Use the track style". */
  onChange(patch: CueStylePatch | null): void;
  disabled?: boolean;
  testId?: string;
}

const pct = (n: number) => Math.round(n * 100);

/** The colour, background and size controls (the cue popover, the toolbar and Track style… use the same). */
export function StyleFields({ value, fallback, onChange, testId, only }: {
  value: CueStyle; fallback: ResolvedCueStyle; onChange: (patch: CueStylePatch) => void; testId: string;
  /** Show one control only (the toolbar's colour, background and size buttons). */
  only?: 'color' | 'background' | 'size';
}) {
  const color = value.color ?? fallback.color;
  const background = value.background ?? fallback.background;
  const size = value.size ?? fallback.size;
  const custom = !CUE_COLOR_SWATCHES.some((s) => s.color === color);
  return (
    <div className="sub-style-fields">
      {(!only || only === 'color') && (
        <div className="sub-style-row">
          <span className="sub-style-label">Colour</span>
          <div className="swatches" role="group" aria-label="Colour">
            {CUE_COLOR_SWATCHES.map((s) => (
              <button key={s.color} type="button" className={`swatch ${color === s.color ? 'on' : ''}`} style={{ background: s.color }} aria-label={s.label} aria-pressed={color === s.color}
                title={s.label} data-testid={`${testId}-color-${s.label.toLowerCase()}`} onClick={() => onChange({ color: s.color })} />
            ))}
            <label className={`swatch custom ${custom ? 'on' : ''}`} title="Another colour" style={custom ? { background: color } : undefined}>
              <input type="color" value={color} aria-label="Another colour" data-testid={`${testId}-color-custom`} onChange={(e) => onChange({ color: e.target.value.toLowerCase() })} />
            </label>
          </div>
        </div>
      )}
      {(!only || only === 'background') && (
        <div className="sub-style-row">
          <label className="sub-style-label" htmlFor={`${testId}-bg`}>Background</label>
          <input id={`${testId}-bg`} type="range" min={0} max={100} step={5} value={pct(background)} aria-valuetext={`${pct(background)} %`} data-testid={`${testId}-background`}
            onChange={(e) => onChange({ background: Number(e.target.value) / 100 })} />
          <span className="mono faint sub-style-value">{pct(background)} %</span>
        </div>
      )}
      {(!only || only === 'size') && (
        <div className="sub-style-row">
          <span className="sub-style-label">Size</span>
          <div className="seg" role="group" aria-label="Size">
            {CUE_SIZE_STEPS.map((s) => (
              <button key={s} type="button" className={`btn ghost sm ${Math.abs(size - s) < 0.001 ? 'on' : ''}`} aria-pressed={Math.abs(size - s) < 0.001}
                data-testid={`${testId}-size-${pct(s)}`} onClick={() => onChange({ size: s })}>{pct(s)}</button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/** Whether a cue sets any style field of its own. */
export const hasOwnStyle = (s: CueStyle | undefined): boolean => Boolean(s && (s.color !== undefined || s.background !== undefined || s.size !== undefined));

export function CueStylePopover({ value, track, onChange, disabled, testId = 'cue-style' }: CueStylePopoverProps) {
  const own = hasOwnStyle(value);
  const color = value.color ?? track.color;
  return (
    <Popover label="Cue style" title={own ? 'Cue style (its own)' : 'Cue style (the track’s)'} disabled={disabled} testId={testId} className={`btn ghost sm icon ${own ? 'on' : ''}`}
      button={<><Palette size={13} /><span className="style-dot" style={{ background: color }} aria-hidden /></>} width={260}>
      {() => (
        <>
          <StyleFields value={value} fallback={track} onChange={onChange} testId={testId} />
          <button type="button" className="btn ghost sm" style={{ width: '100%', marginTop: 4 }} disabled={!own} data-testid={`${testId}-track`}
            onClick={() => onChange({ color: null, background: null, size: null })}>Use the track style</button>
        </>
      )}
    </Popover>
  );
}
